import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, Copy, Download, KeyRound, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useVault } from "@/context/VaultContext";
import { saveFile } from "@/lib/native";

const QR_MAX = 2000;

export function LoginKeyExport() {
  const { exportLoginKey, profile } = useVault();
  const [pass, setPass] = useState("");
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reveal = async () => {
    setBusy(true);
    setError("");
    try {
      setKey(await exportLoginKey(pass));
      setPass("");
    } catch (e) {
      setError(e.message || "Could not create login key");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(key); toast.success("Login key copied"); } catch { toast.error("Copy failed — select the text instead"); }
  };

  const download = async () => {
    const url = URL.createObjectURL(new Blob([key], { type: "text/plain" }));
    await saveFile(url, `aphelion-${profile.alias.replace(/[^a-z0-9]/gi, "").toLowerCase() || "identity"}.aphkey.txt`).catch(() => {});
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  if (!key) {
    return (
      <div className="space-y-2.5">
        <input data-testid="export-pass-input" type="password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Confirm this vault's passphrase"
          className="w-full bg-void border border-rim rounded-secure px-3 py-2 text-sm placeholder-ash/30 focus:outline-none focus:border-stellar/50" />
        {error && <p className="text-xs text-flare" data-testid="export-error">{error}</p>}
        <button onClick={reveal} disabled={busy || !pass} data-testid="export-reveal-btn" className="w-full flex items-center justify-center gap-2 border border-stellar/40 text-stellar rounded-secure py-2 text-sm disabled:opacity-40">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} strokeWidth={1.5} />} Show login key
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="export-result">
      {key.length <= QR_MAX ? (
        <div className="flex justify-center bg-void p-3 rounded-secure border border-glass" data-testid="export-qr"><QRCodeSVG value={key} size={196} bgColor="#050507" fgColor="#0AE2FF" level="L" /></div>
      ) : (
        <p className="text-xs text-ash/50" data-testid="export-qr-too-large">Too many contacts for a QR code — copy the key or save it as a file.</p>
      )}
      <p className="font-mono text-[10px] text-ash/55 break-all max-h-20 overflow-y-auto bg-void border border-glass rounded-secure p-2 select-all" data-testid="export-key-text">{key}</p>
      <div className="flex gap-2">
        <button onClick={copy} data-testid="export-copy-btn" className="flex-1 flex items-center justify-center gap-1.5 border border-rim rounded-secure py-2 text-xs hover:text-stellar hover:border-stellar/40"><Copy size={14} strokeWidth={1.5} /> Copy</button>
        <button onClick={download} data-testid="export-save-btn" className="flex-1 flex items-center justify-center gap-1.5 border border-rim rounded-secure py-2 text-xs hover:text-stellar hover:border-stellar/40"><Download size={14} strokeWidth={1.5} /> Save file</button>
        <button onClick={() => setKey("")} data-testid="export-hide-btn" className="flex items-center justify-center px-3 border border-rim rounded-secure text-xs hover:text-stellar"><EyeOff size={14} strokeWidth={1.5} /></button>
      </div>
      <p className="text-[11px] text-flare/80 leading-relaxed">The key is locked with your passphrase, but treat it like a password. After logging in on the new device, stop using this one — messages are delivered to only one device.</p>
    </div>
  );
}
